/**
 * Shares an album PDF as a FILE — not a link. Copying the link is a separate,
 * explicit action (the chain icon); the share button hands over the actual PDF,
 * mirroring the Apple and Android apps.
 *
 * Web caveat: `navigator.share({ files })` needs both (a) browser support
 * (mobile Safari / Chrome — desktop support is spotty) and (b) the CDN to allow
 * a cross-origin fetch of the PDF. When either is missing we degrade gracefully:
 * share the URL instead, and if even that is unavailable let the caller copy the
 * link. The filename shown in the share sheet is the server's `album.filename`
 * (e.g. "AlbumDesign20.pdf") — the single source of truth across platforms.
 *
 * @returns 'shared' when the file or URL reached the share sheet (incl. the user
 *          dismissing it), or 'fallback' when the caller should copy the link.
 */
export async function shareAlbumFile(
  publicUrl: string,
  filename: string | undefined
): Promise<'shared' | 'fallback'> {
  if (typeof navigator === 'undefined' || !('share' in navigator)) {
    return 'fallback';
  }

  const name = filename?.trim() || 'album.pdf';

  // Preferred path: share the actual PDF file.
  try {
    const response = await fetch(publicUrl);
    if (response.ok) {
      const blob = await response.blob();
      const file = new File([blob], name, { type: 'application/pdf' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
        return 'shared';
      }
    }
  } catch (error) {
    // User dismissed the sheet — done, no fallback.
    if (isAbortError(error)) return 'shared';
    // fetch/CORS failure or unsupported — fall through to URL share.
  }

  // File share unsupported or blocked — share the link.
  try {
    await navigator.share({ url: publicUrl });
    return 'shared';
  } catch (error) {
    if (isAbortError(error)) return 'shared';
    return 'fallback';
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
